import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Spinner,
} from "reactstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import axios from "axios"
import toastr from "toastr"
import profile from "../../assets/images/Logo-SEO.png" // Adjust path as needed
import apiURl from "url"

const Login = () => {
  // Meta title
  document.title = "Login"

  const navigate = useNavigate()

  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedRole, setSelectedRole] = useState("User")

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const [formData, setFormData] = useState({
    email_: "",
    password_: "",
  })

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const loginTime = new Date().getTime()
  const expirationTime = loginTime + 5 * 60 * 60 * 1000

  const handleRoleSelect = role => {
    setSelectedRole(role)
  }

  const login = async e => {
    e.preventDefault()
    setLoading(true)

    if (!selectedRole) {
      toastr.error("Please select a role")
      setLoading(false)
      return
    }
    if (!formData.email_) {
      toastr.error("Email is required")
      setLoading(false)
      return
    }
    if (!formData.password_) {
      toastr.error("Password is required")
      setLoading(false)
      return
    }

    const URL = `${apiURl}/api/auth/login`
    const data = {
      email: formData.email_,
      password: formData.password_,
      role: selectedRole,
    }

    try {
      const response = await fetch(URL, {
        method: "POST",
        body: JSON.stringify(data),
        headers: { "Content-Type": "application/json" },
      })
      const result = await response.json()
      setLoading(false)

      if (response.ok && result.status === "success") {
        const { user } = result

        // Common fields for all roles
        localStorage.setItem("authUser", user.id)
        localStorage.setItem("login_id", user.id)
        localStorage.setItem("login_user_id", user.user_id || user.id)
        localStorage.setItem("email", user.email_)
        localStorage.setItem("role", user.role_)
        localStorage.setItem("phone", user.phone_ || "")

        // Role-specific fields
        switch (selectedRole) {
          case "User":
            localStorage.setItem("full_name", user.full_name || "")
            localStorage.setItem("profile_image", user.image_ || "")
            break
          case "Client":
            localStorage.setItem("full_name", user.full_name || "")
            localStorage.setItem("profile_image", user.image_ || "")
            localStorage.setItem("address", user.address || "")
            localStorage.setItem("customer_type", user.address || "")
            break
          default:
            toastr.error("Unknown role")
            return
        }

        // Handle login time and expiration
        const loginTime = new Date().toISOString()
        const expirationTime = new Date(
          Date.now() + 60 * 60 * 1000
        ).toISOString() // 1 hour session
        localStorage.setItem("loginTime", loginTime)
        localStorage.setItem("expirationTime", expirationTime)

        navigate("/dashboard")
        toastr.success("Login Successful")
      } else {
        toastr.error(result.message || "Invalid email, password, or role")
      }
    } catch (error) {
      setLoading(false)
      toastr.error("Invalid email, password, or role")
      console.error("Login Error:", error)
    }
  }

  return (
    <React.Fragment>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row
            className="justify-content-center"
            style={{ backgroundColor: "#F8F8FB" }}
          >
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary bg-soft">
                  <Row className="d-flex justify-content-center">
                    <Col xl={12} className="text-center">
                      <img
                        src={profile}
                        alt=""
                        width={200}
                        height={100}
                        className="img-fluid"
                        style={{ padding: "25px 0px 25px 0px" }}
                      />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div className="p-2">
                    <Form className="form-horizontal" onSubmit={login}>
                      <div className="mb-3">
                        <Label className="form-label"></Label>
                        <div className="d-flex gap-2">
                          {["User", "Client"].map(role => (
                            <Button
                              key={role}
                              color={
                                selectedRole === role ? "success" : "success"
                              }
                              outline={selectedRole !== role}
                              onClick={() => handleRoleSelect(role)}
                              className="w-100"
                            >
                              {role}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Email</Label>
                        <Input
                          name="email_"
                          className="form-control"
                          placeholder="Enter email"
                          type="email"
                          onChange={handleChange}
                          value={formData.email_}
                        />
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Password</Label>
                        <InputGroup>
                          <Input
                            name="password_"
                            value={formData.password_}
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter Password"
                            onChange={handleChange}
                          />
                          <InputGroupText
                            onClick={togglePasswordVisibility}
                            style={{ cursor: "pointer" }}
                          >
                            <FontAwesomeIcon
                              icon={showPassword ? faEyeSlash : faEye}
                            />
                          </InputGroupText>
                        </InputGroup>
                      </div>

                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="customControlInline"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="customControlInline"
                        >
                          Remember me
                        </label>
                      </div>

                      <div className="mt-3 d-grid">
                        <Button
                          color="success"
                          className="btn-block"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? <Spinner size="sm" /> : "Log In"}
                        </Button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Login
